import type { MessageJson } from 'app/types/entities'

/**
 * SMS delivery state for the inbox alert states (TRK-5461).
 * Twilio accepts a send synchronously and can fail it seconds later at carrier
 * handoff (e.g. error 30034, unregistered 10DLC number) — so "the mutation
 * succeeded" is not "the message arrived". trackable-api persists the provider's
 * raw status on Message (`status`) via Twilio's statusCallback webhook, plus
 * `errorCode`/`errorMessage` (already humanized server-side) on failures.
 * This module maps that raw contract onto the UI's stable delivery shape.
 */

export type MessageDeliveryStatus = `pending` | `sent` | `delivered` | `failed`

export type MessageDelivery = {
  status?: MessageDeliveryStatus
  failureReason?: string
  errorCode?: string
}

export const FALLBACK_FAILURE_REASON = `Carrier rejected this message`

/**
 * Dev-only preview: `localStorage.trkMockSmsDelivery = 'failed'` renders every
 * outbound message as a 30034 failure so the failed states can be demoed
 * without burning real sends. Any other value (or absence) is a no-op.
 */
export const MOCK_DELIVERY_STORAGE_KEY = `trkMockSmsDelivery`
const MOCK_FAILURE_REASON = `Number not registered for A2P 10DLC`

// Raw provider statuses persisted by trackable-api: accepted, queued, sending,
// sent, delivered, undelivered, failed, canceled. The status webhook attaches
// errorCode/errorMessage only on failed/undelivered — both are terminal
// failures and render identically. `canceled` can't occur in our flows (we
// never schedule sends), so it gets no UI treatment.
const FAILED_STATUSES: ReadonlySet<string> = new Set([`failed`, `undelivered`])
const PENDING_STATUSES: ReadonlySet<string> = new Set([`accepted`, `queued`, `sending`])
// Email terminal failures reported by SES (TRK-6056). A complaint means the recipient marked it as spam.
const EMAIL_FAILURE_REASONS: ReadonlyMap<string, string> = new Map([
  [`bounced`, `The email bounced`],
  [`complained`, `The recipient marked this email as spam`],
])

export function isOutboundMessage(message: Pick<MessageJson, `sentByUserId` | `isAgent`>): boolean {
  return Boolean(message.sentByUserId || message.isAgent)
}

function mockFailureEnabled(): boolean {
  try {
    return typeof localStorage !== `undefined` && localStorage.getItem(MOCK_DELIVERY_STORAGE_KEY) === `failed`
    // oxlint-disable-next-line local/require-catch-logging
  } catch {
    return false
  }
}

export function getMessageDelivery(message: MessageJson): MessageDelivery {
  const outbound = isOutboundMessage(message)
  if (mockFailureEnabled()) {
    if (!outbound) return {}
    return { status: `failed`, failureReason: MOCK_FAILURE_REASON, errorCode: `30034` }
  }

  const raw = message.status
  if (!raw) return {}

  const emailFailureReason = EMAIL_FAILURE_REASONS.get(raw)
  if (emailFailureReason) {
    return {
      status: `failed`,
      failureReason: message.errorMessage || emailFailureReason,
      errorCode: message.errorCode ?? undefined,
    }
  }
  if (FAILED_STATUSES.has(raw)) {
    return {
      status: `failed`,
      failureReason: message.errorMessage || FALLBACK_FAILURE_REASON,
      errorCode: message.errorCode ?? undefined,
    }
  }
  if (raw === `delivered`) return { status: `delivered` }
  if (raw === `sent`) return { status: `sent` }
  if (PENDING_STATUSES.has(raw)) return { status: `pending` }
  return {}
}

/** True when this is an outbound message the carrier failed — the alert states. */
export function isFailedDelivery(message: MessageJson): boolean {
  return isOutboundMessage(message) && getMessageDelivery(message).status === `failed`
}
